import React, {useEffect, useState} from "react";
import ReactLoading from "react-loading";
import axios from "axios";
import {confirmAlert} from "react-confirm-alert";
import Button from "devextreme-react/button";
import DataGrid, {
    Column,
    FilterRow,
    Grouping,
    GroupPanel,
    Pager,
    Paging,
    Scrolling,
} from "devextreme-react/data-grid";
import "devextreme/dist/css/dx.light.css";
import {formatDate} from "devextreme/localization";
import excelIcon from "../../assets/images/mgsuite/excel-icon.png";
import FileSaver from "file-saver";
import {useTranslation} from "react-i18next";

const ReservierungsListe = ({filterOptions, searchText, onEdit}) => {
    const { t } = useTranslation();
    const [loaderShow, setLoaderShow] = useState(false);
    const [buchungen, setBuchungen] = useState([]);
    const [buchungenFiltered, setBuchungenFiltered] = useState([]);
    const [lotpreferencesAvailable, setLotpreferencesAvailable] = useState(true);

    const activeRights = JSON.parse(localStorage.getItem("rights"));
    const link = localStorage.getItem('link') + '/api/ReservationService';
    const dateTimeFormat = "dd.MM.yyyy HH:mm";

    const prepareDateFilter = (event) => {
        if (event.parentType === "filterRow" && ["permissionFrom", "permissionTill"].includes(event.dataField)) {
            event.editorOptions.displayFormat = dateTimeFormat;
            event.editorOptions.pickerType = "calendar";
            event.editorOptions.calendarOptions = {
                ...event.editorOptions.calendarOptions,
                firstDayOfWeek: 1,
            };
        }
    };

    axios.defaults.headers.common["Authorization"] = "Bearer " + localStorage.getItem("JWT");
    useEffect(() => {
        loadData(false);
    }, [filterOptions]);

    useEffect(() => {
        filterData();
    }, [searchText]);
    
    const loadData = (doExport) => {
        let selectedDepartment = filterOptions.department;
        let fromFilter = filterOptions.from;
        let tillFilter = filterOptions.till;
        
        if (selectedDepartment == null) {
            return;
        }
        
        setLoaderShow(true);

        const request = {
            departmentId: selectedDepartment?.value ?? '',
            from: fromFilter?.toISOString() ?? '',
            till: tillFilter?.toISOString() ?? '',
        }
        const searchParams = new URLSearchParams(request).toString();
        
        if (doExport === true) {
            const getDataLink = link + '/exportexcel?' + searchParams;
           
            axios.get(getDataLink, { responseType: "blob" }).then((response) => {
                FileSaver.saveAs(response.data, "Reservations.xlsx");
                setLoaderShow(false);
            })
            .catch((error) => {
                console.error("Error while loading Data", error);
                setLoaderShow(false);
            });
        } else {
            const getDataLink = link + '?' + searchParams;
           
            axios.get(getDataLink, {}).then((response) => {
                setBuchungen(response.data.data);
                setLotpreferencesAvailable(response.data.lotPreferenceAvailable);
                filterData(response.data.data);
                setLoaderShow(false);
            })
            .catch((error) => {
                console.error("Error while loading Data", error);
                setLoaderShow(false);
            });
        }
    }

    const filterData = (b) => {
        let _buchungen = b ?? buchungen;
        const searchTextNormalized = searchText.toLowerCase();
        setBuchungenFiltered(_buchungen.filter(b =>
            b.numberPlate.toLowerCase().includes(searchTextNormalized) ||
            b.firstname?.toLowerCase()?.includes(searchTextNormalized) ||
            b.lastname?.toLowerCase()?.includes(searchTextNormalized) ||
            b.company?.toLowerCase()?.includes(searchTextNormalized) ||
            b.eMail?.toLowerCase()?.includes(searchTextNormalized)
        ));
    }

    const deleteEntry = (entry) => {
        confirmAlert({
            message: t("allgemein.bestaetigen"),
            buttons: [
                {
                    label: t("allgemein.ja"),
                    onClick: () =>
                        axios
                            .delete(link + '?id=' + entry.id)
                            .then((response) => {
                                const buchungenWithoutDeleted = buchungen.filter(b => b.id !== entry.id);
                                setBuchungen(buchungenWithoutDeleted);
                                filterData(buchungenWithoutDeleted);
                            })

                            .catch((error) => {
                                console.error("Error while deleting", error);
                            }),
                },
                {
                    label: t("allgemein.nein"),
                },
            ],
        }); 
    }
    
    const renderActions = ({data}) => (
        <div>
            {activeRights.find((item) => item.includes("ReservationService.Update")) && (
                <i
                    className="bx bx-edit bx-sm"
                    id="pop-up-button"
                    title={t("allgemein.Bearbeiten")}
                    onClick={() => onEdit(data.id)}
                />
            )}
            {activeRights.find((item) => item.includes("ReservationService.Delete")) && (
                <i
                    className="bx bx-trash bx-sm"
                    id="pop-up-button"
                    style={{marginLeft: "10px"}}
                    title={t("allgemein.Loeschen")}
                    onClick={() => deleteEntry(data)}
                />
            )}
        </div>
    );

    let header = t("reservationService.reservationList");
    if (filterOptions.department?.value > 0) {
        header += ` ${t("userDepartments.department")}: ${filterOptions.department.label}`;
    }
    if (filterOptions.from) {
        header += ` ${t("allgemein.von")}: ${formatDate(filterOptions.from, dateTimeFormat)}`;
    }
    if (filterOptions.till) {
        header += ` ${t("allgemein.bis")}: ${formatDate(filterOptions.till, dateTimeFormat)}`;
    }
    return (
        <div id="settings-page-view-container" className="reservation-list-page">
            <div className="einstellungen-box reservation-list-box">
                <div className="box-name">{header}</div>
                <div className="reservation-list-content">
                    {loaderShow === true && (
                        <div style={{position: "absolute"}}>
                            <ReactLoading
                                type={"bars"}
                                color={"#2a3042"}
                                height={80}
                                width={80}
                                name={"Loading"}
                            />
                        </div>
                    )}

                    <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "15px", marginRight: "15px", marginBottom: "10px"}}>
                        <Button id="exportButton" icon={excelIcon} text={t("reservationService.exportData")} onClick={() => loadData(true)} />
                    </div>
                    <DataGrid
                        className="reservation-list-grid"
                        dataSource={buchungenFiltered}
                        keyExpr="id"
                        height="100%"
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        columnAutoWidth={true}
                        showBorders={true}
                        onEditorPreparing={prepareDateFilter}
                    >
                        <Scrolling mode="standard" />
                        <GroupPanel visible={true} emptyPanelText={t("reservationService.groupPanelEmptyText")} />
                        <Grouping />
                        <FilterRow visible={true} />
                        <Paging defaultPageSize={100} />
                        <Pager visible={true} showNavigationButtons={true} showInfo={true} infoText={t("reservationService.pagerInfoText")} />
                        <Column dataField="creatorUserName" caption={t("reservationService.listHeader.creator")} />
                        <Column dataField="departmentName" caption={t("reservationService.listHeader.department")} />
                        <Column dataField="numberPlate" caption={t("reservationService.listHeader.numberPlate")} />
                        <Column dataField="permissionFrom" dataType="datetime" format={dateTimeFormat} width={200} caption={t("reservationService.listHeader.from")} />
                        <Column dataField="permissionTill" dataType="datetime" format={dateTimeFormat} width={200} caption={t("reservationService.listHeader.till")} />
                        {lotpreferencesAvailable && (
                            <Column dataField="parkingLotPreference" caption={t("reservationService.listHeader.lotPreference")} />
                        )}
                        <Column dataField="scenarioName" caption={t("reservationService.listHeader.scenario")} />
                        <Column dataField="company" caption={t("reservationService.listHeader.company")} />
                        <Column
                            caption={t("reservationService.listHeader.name")}
                            calculateCellValue={(booking) => [booking.firstname, booking.lastname].filter(Boolean).join(" ")}
                        />
                        <Column cellRender={renderActions} allowFiltering={false} allowSorting={false} allowGrouping={false} />
                    </DataGrid>
                </div>
            </div>
        </div>
    );
};

export default ReservierungsListe;
